import { useState, useEffect, lazy, Suspense, createContext, useContext, useCallback } from 'react';
import { Outlet } from 'react-router-dom';
import SiteHeader from './SiteHeader';
import Watermark from './Watermark';
import DocSidebar from './DocSidebar';

const SearchModal = lazy(() => import('./SearchModal'));
const DiagramFullscreen = lazy(() => import('./DiagramFullscreen'));

interface DiagramModalState {
  isOpen: boolean;
  svg: string;
  title: string;
}

interface DiagramModalContextType {
  openDiagram: (svg: string, title: string) => void;
  closeDiagram: () => void;
}

export const DiagramModalContext = createContext<DiagramModalContextType>({
  openDiagram: () => {},
  closeDiagram: () => {},
});

export const useDiagramModal = () => useContext(DiagramModalContext);

export default function DocLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [diagramModal, setDiagramModal] = useState<DiagramModalState>({
    isOpen: false,
    svg: '',
    title: '',
  });

  const openDiagram = useCallback((svg: string, title: string) => {
    setDiagramModal({ isOpen: true, svg, title });
  }, []);

  const closeDiagram = useCallback(() => {
    setDiagramModal(prev => ({ ...prev, isOpen: false }));
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const isMac = /Mac|iPhone|iPad/.test(navigator.platform);
      const cmdK = (isMac ? e.metaKey : e.ctrlKey) && e.key.toLowerCase() === 'k';
      if (cmdK) {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
      if (e.key === 'Escape') {
        setSearchOpen(false);
        // Note: Diagram modal handles its own Escape, but this ensures it's closed
        // if somehow the event bubbles (though modal stops propagation)
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <DiagramModalContext.Provider value={{ openDiagram, closeDiagram }}>
      <div className="min-h-screen bg-paper-50">
        <SiteHeader
          onSearchClick={() => setSearchOpen(true)}
          onMenuClick={() => setSidebarOpen((v) => !v)}
        />
        <Watermark />
        <div className="doc-shell">
          <DocSidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
          <main className="doc-main">
            <Outlet />
          </main>
        </div>
        {searchOpen && (
          <Suspense fallback={null}>
            <SearchModal onClose={() => setSearchOpen(false)} />
          </Suspense>
        )}
        {diagramModal.isOpen && (
          <Suspense fallback={null}>
            <DiagramFullscreen
              svg={diagramModal.svg}
              title={diagramModal.title}
              onClose={closeDiagram}
            />
          </Suspense>
        )}
      </div>
    </DiagramModalContext.Provider>
  );
}
